import { inject, Injectable } from '@angular/core';

import { BreakPointManager } from '@23degrees/shared/manager/breakpoint/breakpoint.manager';
import { StyleService } from '../style/style.service';

import type { FontFormat } from '@23degrees/shared-code/models';

import { type DeltaInsertOp, OpToHtmlConverter, QuillDeltaToHtmlConverter, convertDeltaToHTML } from '@23degrees/quill';
import { convertQuillToString } from '@23degrees/shared-code/content/content-utilities';
import { GlobalService } from '../global';
const camelToKebab = (str) => str.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);

/**
 *
 * @param styleObj
 * @returns
 */
const styleToCSS = (styleObj: any) => {
  const styles = Object.entries(styleObj);
  const cssStyles = styles.map(([key, value]) => `${camelToKebab(key)}:${value}`);
  return cssStyles.join(';');
};

export interface QuillDelta {
  ops: DeltaInsertOp[];
}

@Injectable({
  providedIn: 'root',
})
export class QuillRendererService {
  private htmlCache = new Map<string, string>();
  private styleCache = new Map<string, string>();

  private breakPointManager: BreakPointManager = inject(BreakPointManager);
  private styleService: StyleService = inject(StyleService)
  private globalService: GlobalService = inject(GlobalService);


  convertDelta(op, slug?: string, ignoreScaling: boolean = false) {
    let sizeFactor: number = 1;

    if (slug && !ignoreScaling) {
      sizeFactor = this.breakPointManager.getSizeFactor(slug);
      // console.log("SizeFactor Wysiwig",this.sizeFactor, " for slug: ",slug);
    }

    const converter = new QuillDeltaToHtmlConverter(op, { inlineStyles: true, sizeFactor, escapeLink: this.globalService.escapeMediaLink });
    converter.renderCustomWith((customOp) => {
      if (customOp.insert.type === 'embedVisual') {
        /**
         * Render Embed Visual
         */
        const val = customOp.insert.value;
        return `<embed-visual data-slug="${val.slug}" data-node-id="${val.nodeId}"></embed-visual>`;
      }
      if (customOp.insert.type === 'inlineValue') {
        /**
         * Render Inline Value
         */
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor, escapeLink: this.globalService.escapeMediaLink });
        const val = customOp.insert.value;
        const classes = opToHtml.getCssClasses();
        const styles = opToHtml.getCssStyles();
        const html = opToHtml.getHtml();

        return `<span data-field-name="${val.fieldName}" data-node-id="${val.nodeId}" class="${classes.join(' ')}">${html}</span>`;
      }
      if (customOp.insert.type === 'dynamicImage') {
        /**
         * Render Image From Data Field
         */
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor, escapeLink: this.globalService.escapeMediaLink });
        const val = customOp.insert.value;
        const classes = opToHtml.getCssClasses();
        const styles = opToHtml.getCssStyles();
        const html = opToHtml.getHtml();
        return `<span data-field-name="${val.fieldName}" data-node-id="${val.nodeId}" class="${classes.join(' ')}">${html}</span>`;
      }
      if (customOp.insert.type === 'dynamicIframe') {
        /**
         * Render Link From Data Field
         */
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor, escapeLink: this.globalService.escapeMediaLink });
        const val = customOp.insert.value;
        const classes = opToHtml.getCssClasses();
        const styles = opToHtml.getCssStyles();
        const html = opToHtml.getHtml();
        return `<span data-field-name="${val.fieldName}" data-node-id="${val.nodeId}" class="${classes.join(' ')}">${html}</span>`;
      }

      if (customOp.insert.type === 'dynamicLink') {
        /**
         * Render Link From Data Field
         */
        const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor, escapeLink: this.globalService.escapeMediaLink });
        const val = customOp.insert.value;
        const classes = opToHtml.getCssClasses();
        const styles = opToHtml.getCssStyles();
        const html = opToHtml.getHtml();
        return `<span data-field-name="${val.fieldName}" data-node-id="${val.nodeId}" class="${classes.join(' ')}">${html}</span>`;
      }
      return 'Unmanaged custom blot!';
    });

    return converter;
  }

  convertDeltaToHTML = (quillDelta: string, styleObj: FontFormat, dataRow: any, sizeFactor: number = 1, content?: any, customStyle: any = {}) => {
    return convertDeltaToHTML(quillDelta, styleObj, dataRow, sizeFactor, content, customStyle);
  };

  // convertDeltaToHTMLTemplate = (quillDelta: any, styleObj: FontFormat, dataRow: any, sizeFactor: number = 1, content?: any) => {
  //   const deltaHash = murmur(quillDelta);
  //   if (!this.htmlCache.has(deltaHash)) {
  //     const renderInlineValue = (customOp) => {
  //       try {
  //         if (customOp.insert.type === 'inlineValue') {
  //           const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor, escapeLink: this.globalService.escapeMediaLink });
  //           const html = opToHtml.getHtml();

  //           return html.replace('[object Object]', '{inlineValue}');
  //         }
  //         if (customOp.insert.type === 'inlineReference') {
  //           const opToHtml = new OpToHtmlConverter(customOp, { inlineStyles: true, sizeFactor, escapeLink: this.globalService.escapeMediaLink });
  //           const html = opToHtml.getHtml();

  //           return html.replace('[object Object]', '{inlineReference}');
  //         }
  //         return 'Unmanaged custom blot!';
  //       } catch (error) {
  //         console.log('[QuillDeltaToHtmlConverter] error:', error);
  //         return 'Error in custom blot!';
  //       }
  //     };

  //     let delta: QuillDelta = { ops: [] };
  //     if (typeof quillDelta === 'string') {
  //       delta = JSON.parse(quillDelta);
  //     }

  //     const converter = new QuillDeltaToHtmlConverter(delta.ops, { inlineStyles: true, sizeFactor, escapeLink: this.globalService.escapeMediaLink });
  //     converter.renderCustomWith(renderInlineValue);

  //     this.htmlCache.set(deltaHash, converter.convert());
  //   }

  //   const quillHTML = this.htmlCache.get(deltaHash);

  //   const cssHash = murmur(JSON.stringify(styleObj) + sizeFactor);
  //   if (!this.styleCache.has(cssHash)) {
  //     const htmlStyle = this.styleService.convertFontFormat(styleObj, undefined, undefined, true);
  //     delete htmlStyle.color;

  //     this.styleCache.set(cssHash, styleToCSS(htmlStyle));
  //   }

  //   const cssStyle = this.styleCache.get(cssHash);

  //   const htmlStr = `<div style="${cssStyle}">${quillHTML}</div>`;

  //   // const inlineValue = InlineValue.create(customOp);
  //   // const inlineStr = inlineValue.resolveFields(dataRow, '');

  //   // const inlineValue = InlineReference.create(customOp);
  //   // const inlineStr = inlineValue.resolveFieldsReference(dataRow, '', content);

  //   return htmlStr;
  // };

  // opToHtml(op) {
  //   const converter = new OpToHtmlConverter(op);
  //   return converter.getHtml();
  // }

  /**
   * Convert Quill editor output to plain string
   * Example: deltaToString(content.description)
   *
   * @param delta Quill Delta string
   */
  public deltaToString(delta) {
    return convertQuillToString(delta);
  }
}
